import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Fab,
  IconButton,
  Avatar,
  Badge,
  Typography,
  TextField,
  InputBase,
  Tooltip,
  Chip,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  ArrowBack,
  Add,
  AutoAwesome,
  OpenInFull,
  CloseFullscreen,
  Fullscreen,
  FullscreenExit,
  Search,
  Forum,
  VolumeUp,
  VolumeOff,
  DoneAll,
  Done,
  AttachFile,
  ChatBubbleOutline,
  InsertDriveFile,
  PictureAsPdf,
  Image as ImageIcon,
  SupportAgent,
  Download,
  DeleteOutline,
  InfoOutlined,
  OpenInNew,
  Groups,
  GroupAdd,
  People,
} from "@mui/icons-material";
import ConfirmModal from "./modals/ConfirmModal";
import { useAuth } from "../contexts/AuthContext";
import { useRouter } from "../contexts/RouterContext";
import sininhoAvatar from "../images/sininho/sininho_avatar.png";
import sininhoFull from "../images/sininho/sininho.png";
import { colors } from "../theme/designSystem";
import { aiChatService, MyAgent } from "../services/aiChatService";
import { chatService, ChatContact, ChatGroup, ChatGroupMember, ChatGroupReaders, ChatMessage, ChatAttachment, CHAT_OPEN_EVENT, CHAT_UNREAD_EVENT, chatUnreadStore } from "../services/chatService";
import AgentConversation, { AgentConversationHandle, AgentView } from "./chat/AgentConversation";
import NewGroupModal from "./chat/NewGroupModal";
import GroupMembersModal from "./chat/GroupMembersModal";
import SupportConversation, { SupportConversationHandle } from "./chat/SupportConversation";
import { ASSISTANTS_STATE_EVENT, assistantsStore, CHAT_DOCK_EVENT, chatDockStore } from "./chat/chatDock";
import { AreaHeader, AuditNotice, ComposerFrame, ComposerIconButton, listColumnSx, listHeaderSx, listRowSx, SendButton } from "./assistants/AssistantUi";
import { supportService, SupportAgentInfo } from "../services/supportService";
import {
  armAudioOnFirstGesture,
  isChatMuted,
  playIncomingTone,
  requestDesktopNotifications,
  setChatMuted,
  showDesktopNotification,
} from "./chat/notificationSound";

// Chat do tenant (RN-551): pessoas da entidade e assistentes de IA na mesma lista de contatos.
// Tocar numa pessoa abre a conversa direta (api/chat); tocar num assistente abre o modelo de chat
// de IA original (AgentConversation, api/ai-chat), intocado. Tempo real na fase 1 é por polling:
// não-lidos a cada 5 s, mensagens da conversa aberta a cada 3 s (consultas leves, por usuário).

// Páginas onde o FAB não deve aparecer: as deslogadas e TODAS as de impressão.
// As de impressão saem pelo sufixo, não por lista: a enumeração manual esquecia a
// página nova a cada entrega (notes-print e assessment-print entraram sem ela, e o
// FAB acabou dentro do documento impresso — achado do Hilton, 31/08).
// Páginas públicas do paciente/visitante também ficam fora, mesmo com sessão aberta em outra aba
// (o Hilton viu o balão na confirmação de check-in, 07/09/2026).
const HIDDEN_PAGES = new Set<string>(["login", "landing", "alias-register", "checkin", "cadastro", "set-password", "patient-intake", "patient-quick-register"]);

const isHiddenPage = (page: string): boolean =>
  HIDDEN_PAGES.has(page) || page.endsWith("-print");

const SESSION_HIDE_KEY = "aiChatHidden";
const UNREAD_POLL_MS = 5000;
const THREAD_POLL_MS = 3000;

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "IA";

// Cor estável por pessoa (a mesma em toda a sessão), fora da paleta primária para não confundir com o agente.
const PERSON_COLORS = ["#5C7CFA", "#845EF7", "#F76707", "#E64980", "#0CA678", "#F59F00", "#1098AD", "#7048E8"];
const personColor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[h % PERSON_COLORS.length];
};

// Datas da API vêm no horário da entidade sem fuso ("2026-09-06T14:03:00"); o Date() do browser as
// lê como local, que é como devem ser exibidas.
const parse = (iso: string): Date => new Date(iso);

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Hora se for hoje, "Ontem", dia da semana na mesma semana, senão dd/mm — como o WhatsApp.
const listTime = (iso: string): string => {
  const d = parse(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (isSameDay(d, now)) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) return "Ontem";
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays < 7) return d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const dayLabel = (iso: string): string => {
  const d = parse(iso);
  const now = new Date();
  if (isSameDay(d, now)) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) return "Ontem";
  return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
};

const hourOf = (iso: string): string => parse(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const ACCEPTED_FILES = ".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.ods,.csv,.ppt,.pptx";
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fileIcon = (a: ChatAttachment) => {
  if (a.isImage) return <ImageIcon fontSize="small" />;
  if (a.contentType.includes("pdf") || a.name.toLowerCase().endsWith(".pdf")) return <PictureAsPdf fontSize="small" />;
  return <InsertDriveFile fontSize="small" />;
};

// Abre/baixa o anexo pelo proxy autenticado (a URL direta não leva o token).
const openAttachment = async (m: ChatMessage) => {
  if (!m.attachment) return;
  try {
    const blob = await chatService.attachment(m.id, false);
    const url = URL.createObjectURL(blob);
    if (m.attachment.isImage || m.attachment.contentType.includes("pdf")) {
      window.open(url, "_blank", "noopener");
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = m.attachment.name;
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    // sem rede: nada a fazer além de não travar
  }
};

// Ordem da conversa = hora do servidor (sentAt), nunca a ordem de chegada ao navegador.
const mergeMessages = (prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] => {
  const byId = new Map(prev.map((m) => [m.id, m]));
  incoming.forEach((m) => byId.set(m.id, m));
  return Array.from(byId.values()).sort((a, b) => a.sentAt.localeCompare(b.sentAt) || a.id.localeCompare(b.id));
};

const maxSentAt = (list: ChatMessage[]): string | null =>
  list.reduce<string | null>((acc, m) => (acc === null || m.sentAt > acc ? m.sentAt : acc), null);

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Menção em grupo (RN-551h fase 3): "@Nome Completo" ou "@Primeiro" seguido de fim de palavra, como o servidor avalia.
const mentionRegexFor = (members: ChatGroupMember[]): RegExp | null => {
  const names = Array.from(new Set(members.flatMap((m) => [m.name.trim(), m.name.trim().split(/\s+/)[0]]).filter(Boolean))).sort((a, b) => b.length - a.length);
  return names.length === 0 ? null : new RegExp(`@(${names.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}])`, "giu");
};

type Filter = "all" | "people" | "groups" | "agents";

type Selected =
  | { kind: "person"; id: string }
  | { kind: "group"; id: string }
  | { kind: "agent"; id: string }
  | { kind: "support"; id: string }
  | null;

// Cor estável por grupo, na família do primário, para a lista distinguir grupo de pessoa de relance.
const GROUP_COLORS = ["#0B7285", "#087F5B", "#5C940D", "#862E9C", "#C92A2A", "#E67700"];
const groupColor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GROUP_COLORS[h % GROUP_COLORS.length];
};

// Chamados com resposta nova são consultados com menos frequência que o chat interno (polling mais leve).
const SUPPORT_UNREAD_POLL_MS = 60000;

const ChatLauncher: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { currentPage, navigateTo } = useRouter();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const entityId = user?.entityId || "";

  const [agents, setAgents] = useState<MyAgent[]>([]);
  const [avatars, setAvatars] = useState<Record<string, string>>({}); // agentId -> object URL
  const [photos, setPhotos] = useState<Record<string, string>>({}); // userId -> object URL
  // Foto que falhou (sem arquivo no storage) não é pedida de novo a cada atualização da lista
  const failedPhotos = useRef<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<boolean>(() => sessionStorage.getItem(SESSION_HIDE_KEY) === "1");
  const [open, setOpen] = useState(false);
  // Encaixado na página Assistentes: o painel é desenhado lá, sem FAB e sem janela flutuante
  const [dockEl, setDockEl] = useState<HTMLElement | null>(() => chatDockStore.element);
  const docked = dockEl !== null;
  const visible = open || docked;
  // Com o app Assistentes aberto (modal em tela cheia), o FAB e a janela flutuante saem da frente
  const [assistantsOpen, setAssistantsOpenState] = useState<boolean>(() => assistantsStore.open);
  const [expanded, setExpanded] = useState(false); // duas colunas (só desktop)
  const [maximized, setMaximized] = useState(false); // tela cheia no desktop (no celular o painel já ocupa a tela)
  const [selected, setSelected] = useState<Selected>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<ChatContact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [groups, setGroups] = useState<ChatGroup[]>([]);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  // "Lido por" de uma mensagem minha no grupo (fase 2): abre ao tocar no tique
  const [readersFor, setReadersFor] = useState<ChatMessage | null>(null);
  const [readers, setReaders] = useState<ChatGroupReaders | null>(null);
  // Membros do grupo aberto: alimentam a menção com @ (sugestões e destaque)
  const [groupMembers, setGroupMembers] = useState<ChatGroupMember[]>([]);
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [otherReadAt, setOtherReadAt] = useState<string | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({}); // messageId -> object URL da miniatura
  const [deleteTarget, setDeleteTarget] = useState<ChatMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [muted, setMuted] = useState<boolean>(() => isChatMuted());
  const [agentView, setAgentView] = useState<AgentView>("conversations");
  const agentRef = useRef<AgentConversationHandle | null>(null);
  const supportRef = useRef<SupportConversationHandle | null>(null);
  const [supportAgents, setSupportAgents] = useState<SupportAgentInfo[]>([]);
  const [supportUnread, setSupportUnread] = useState(0);
  const supportUnreadRef = useRef(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const prevUnreadRef = useRef<number | null>(null);
  const openRef = useRef(false);
  // Marca d'água do polling: última hora vinda do SERVIDOR (GET), não do próprio envio — senão uma
  // mensagem do outro gravada segundos antes da minha ficava fora da janela para sempre.
  const watermarkRef = useRef<string | null>(null);
  // Ids já exibidos: o servidor devolve de novo a mensagem do mesmo segundo da marca d'água (a hora
  // trafega sem fração), então "nova" é só a de id desconhecido — vale para o som e o marcar-lido.
  const knownIdsRef = useRef<Set<string>>(new Set());
  const selectedPersonRef = useRef<string | null>(null);
  const selectedGroupRef = useRef<string | null>(null);
  // Contatos já na lista: conversa não-lida de alguém fora dela (o Sininho no primeiro aviso) recarrega a lista
  const contactIdsRef = useRef<Set<string>>(new Set());
  const groupIdsRef = useRef<Set<string>>(new Set());
  const reloadedForRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    contactIdsRef.current = new Set(contacts.map((c) => c.userId));
  }, [contacts]);
  useEffect(() => {
    groupIdsRef.current = new Set(groups.map((g) => g.threadId));
  }, [groups]);

  // Monta (carrega agentes + escuta os eventos globais) mesmo com o FAB oculto; o FAB em si
  // só aparece quando não dispensado. Assim o botão do topo e "Meus agentes" abrem o chat.
  // Permissão `chat` (functionality central, grants por perfil): sem ela nem balão nem polling
  const canChat = !!user && ((user.allowedPages || []).includes("chat") || (user.permissions || []).includes("chat"));
  const canLoad = isAuthenticated && !!entityId && canChat && !isHiddenPage(currentPage);

  useEffect(() => {
    openRef.current = visible;
  }, [visible]);

  useEffect(() => {
    const handler = (e: Event) => {
      const element = (e as CustomEvent<{ element: HTMLElement | null }>).detail?.element ?? null;
      setDockEl(element);
      // A janela flutuante não reabre sozinha ao sair da página Assistentes
      if (element) setOpen(false);
    };
    window.addEventListener(CHAT_DOCK_EVENT, handler);
    return () => window.removeEventListener(CHAT_DOCK_EVENT, handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const isOpen = !!(e as CustomEvent<{ open: boolean }>).detail?.open;
      setAssistantsOpenState(isOpen);
      if (isOpen) setOpen(false);
    };
    window.addEventListener(ASSISTANTS_STATE_EVENT, handler);
    return () => window.removeEventListener(ASSISTANTS_STATE_EVENT, handler);
  }, []);

  useEffect(() => {
    knownIdsRef.current = new Set(messages.map((m) => m.id));
  }, [messages]);

  useEffect(() => {
    selectedPersonRef.current = selected?.kind === "person" ? selected.id : null;
    selectedGroupRef.current = selected?.kind === "group" ? selected.id : null;
  }, [selected]);

  useEffect(() => {
    if (canLoad) armAudioOnFirstGesture();
  }, [canLoad]);

  useEffect(() => {
    if (!canLoad) return;
    let active = true;
    aiChatService
      .myAgents(entityId)
      .then((list) => active && setAgents(list))
      .catch(() => active && setAgents([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, entityId]);

  useEffect(() => {
    if (!canLoad) return;
    let active = true;
    supportService
      .agents()
      .then((list) => active && setSupportAgents(list))
      .catch(() => active && setSupportAgents([]));
    return () => {
      active = false;
    };
  }, [canLoad, entityId]);

  useEffect(() => {
    if (!canLoad || supportAgents.length === 0) return;
    let active = true;
    const poll = () =>
      supportService
        .unread()
        .then((n) => active && setSupportUnread(n))
        .catch(() => undefined);
    poll();
    const timer = window.setInterval(poll, SUPPORT_UNREAD_POLL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [canLoad, supportAgents.length]);

  // Abertura pela seção "Meus agentes" do menu (evento global) — abre o painel no agente pedido,
  // mesmo que o FAB esteja oculto.
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<{ agentId: string }>).detail?.agentId;
      if (agents.some((a) => a.id === id)) {
        if (!chatDockStore.element) setOpen(true);
        setSelected({ kind: "agent", id });
      }
    };
    window.addEventListener("clinic4us:open-agent-chat", handler);
    return () => window.removeEventListener("clinic4us:open-agent-chat", handler);
  }, [agents]);

  // Abertura pelo botão do cabeçalho: abre o painel e devolve o FAB se estava oculto.
  useEffect(() => {
    const handler = () => {
      // Encaixado, o chat já está na tela
      if (chatDockStore.element) return;
      sessionStorage.removeItem(SESSION_HIDE_KEY);
      setDismissed(false);
      setOpen(true);
      requestDesktopNotifications();
    };
    window.addEventListener(CHAT_OPEN_EVENT, handler);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, handler);
  }, []);

  // Carrega os avatares (blob -> object URL) dos agentes que têm imagem; revoga ao desmontar/trocar.
  useEffect(() => {
    const withAvatar = agents.filter((a) => a.hasAvatar);
    if (withAvatar.length === 0) return;
    let active = true;
    const created: string[] = [];
    Promise.all(
      withAvatar.map(async (a) => {
        try {
          const url = await aiChatService.avatar(a.id);
          return [a.id, url] as const;
        } catch {
          return null;
        }
      })
    ).then((pairs) => {
      if (!active) {
        pairs.forEach((p) => p && URL.revokeObjectURL(p[1]));
        return;
      }
      const map: Record<string, string> = {};
      pairs.forEach((p) => {
        if (p) {
          map[p[0]] = p[1];
          created.push(p[1]);
        }
      });
      setAvatars(map);
    });
    return () => {
      active = false;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [agents]);

  // ---------- Contatos e não-lidos ----------
  const publishUnread = useCallback((total: number) => {
    setTotalUnread(total);
    const combined = total + supportUnreadRef.current;
    chatUnreadStore.total = combined;
    window.dispatchEvent(new CustomEvent(CHAT_UNREAD_EVENT, { detail: { total: combined } }));
  }, []);

  useEffect(() => {
    supportUnreadRef.current = supportUnread;
    const combined = totalUnread + supportUnread;
    chatUnreadStore.total = combined;
    window.dispatchEvent(new CustomEvent(CHAT_UNREAD_EVENT, { detail: { total: combined } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportUnread]);

  // Pessoas e grupos vêm juntos: o badge soma os dois
  const loadContacts = useCallback(async () => {
    try {
      const [list, groupList] = await Promise.all([chatService.contacts(), chatService.groups().catch(() => [] as ChatGroup[])]);
      setContacts(list);
      setGroups(groupList);
      publishUnread(list.reduce((s, c) => s + c.unread, 0) + groupList.reduce((s, g) => s + g.unread, 0));
    } catch {
      // sem rede ou sessão expirada: mantém a lista anterior
    } finally {
      setContactsLoaded(true);
    }
  }, [publishUnread]);

  const pollUnread = useCallback(async () => {
    try {
      const u = await chatService.unread();
      // O que está aberto na tela não conta como não-lido (o polling da conversa cuida dele)
      const viewing = openRef.current ? selectedPersonRef.current : null;
      const viewingGroup = openRef.current ? selectedGroupRef.current : null;
      const isGroup = (t: { kind?: string }) => t.kind === "group";
      const viewedUnread =
        (viewing ? u.threads.find((x) => !isGroup(x) && x.otherUserId === viewing)?.unread ?? 0 : 0) +
        (viewingGroup ? u.threads.find((x) => isGroup(x) && x.threadId === viewingGroup)?.unread ?? 0 : 0);
      const visibleTotal = u.total - viewedUnread;
      // Chegou mensagem com a lista à vista, ou de alguém (ou grupo) que ainda não está na lista: recarrega
      // para trazer a prévia, a ordenação e o contato novo
      // Uma vez por remetente: quem continua fora da lista depois de recarregar (ex.: saiu da clínica) não repete a carga
      const unknown = u.threads
        .map((t) => (isGroup(t) ? t.threadId : t.otherUserId))
        .filter((id, i) => id && !(isGroup(u.threads[i]) ? groupIdsRef.current : contactIdsRef.current).has(id) && !reloadedForRef.current.has(id));
      unknown.forEach((id) => reloadedForRef.current.add(id));
      const unknownSender = unknown.length > 0;
      if ((visibleTotal > chatUnreadStore.total && openRef.current && !viewing && !viewingGroup) || unknownSender) {
        await loadContacts();
        return;
      }
      publishUnread(visibleTotal);
      // Reflete no contato e no grupo sem recarregar a lista inteira
      const online = new Set(u.online ?? []);
      setContacts((prev) =>
        prev.map((c) => {
          const t = u.threads.find((x) => !isGroup(x) && x.otherUserId === c.userId);
          const unread = c.userId === viewing ? 0 : t ? t.unread : 0;
          return { ...c, unread, lastMessageAt: t?.lastMessageAt ?? c.lastMessageAt, online: online.has(c.userId) };
        })
      );
      setGroups((prev) =>
        prev.map((g) => {
          const t = u.threads.find((x) => isGroup(x) && x.threadId === g.threadId);
          const unread = g.threadId === viewingGroup ? 0 : t ? t.unread : 0;
          return { ...g, unread, lastMessageAt: t?.lastMessageAt ?? g.lastMessageAt };
        })
      );
    } catch {
      // silencioso: o próximo tick tenta de novo
    }
  }, [publishUnread, loadContacts]);

  useEffect(() => {
    if (!canLoad) return;
    loadContacts();
    const timer = window.setInterval(pollUnread, UNREAD_POLL_MS);
    return () => window.clearInterval(timer);
  }, [canLoad, loadContacts, pollUnread]);

  // Fotos dos contatos que têm foto (miniatura via proxy autenticado)
  useEffect(() => {
    const withPhoto = contacts.filter((c) => c.hasPhoto && !photos[c.userId] && !failedPhotos.current.has(c.userId));
    if (withPhoto.length === 0) return;
    let active = true;
    Promise.all(
      withPhoto.map(async (c) => {
        try {
          return [c.userId, await chatService.photo(c.userId)] as const;
        } catch {
          failedPhotos.current.add(c.userId);
          return null;
        }
      })
    ).then((pairs) => {
      if (!active) return;
      setPhotos((prev) => {
        const next = { ...prev };
        pairs.forEach((p) => {
          if (p) next[p[0]] = p[1];
        });
        return next;
      });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  // Mensagem nova = total de não-lidos subiu → som suave (se não silenciado). A primeira leitura
  // só calibra, para não tocar ao abrir a página com pendências antigas.
  useEffect(() => {
    if (prevUnreadRef.current !== null && totalUnread > prevUnreadRef.current) {
      if (!muted) playIncomingTone();
      showDesktopNotification("Nova mensagem no chat");
    }
    prevUnreadRef.current = totalUnread;
  }, [totalUnread, muted]);

  // ---------- Conversa direta e grupo ----------
  const selectedContact = selected?.kind === "person" ? contacts.find((c) => c.userId === selected.id) ?? null : null;
  const selectedGroup = selected?.kind === "group" ? groups.find((g) => g.threadId === selected.id) ?? null : null;
  const canOpenPatient = (user?.allowedPages || []).includes("patient-register");
  const selectedAgent = selected?.kind === "agent" ? agents.find((a) => a.id === selected.id) ?? null : null;
  // A conversa aberta, seja com pessoa ou grupo: o painel de mensagens é o mesmo
  const conversation = selectedContact
    ? { kind: "person" as const, key: selectedContact.userId, name: selectedContact.name, isSystem: !!selectedContact.isSystem, online: selectedContact.online }
    : selectedGroup
      ? { kind: "group" as const, key: selectedGroup.threadId, name: selectedGroup.title, isSystem: false, online: true }
      : null;

  const publishTotal = (next: number) => {
    chatUnreadStore.total = next;
    window.dispatchEvent(new CustomEvent(CHAT_UNREAD_EVENT, { detail: { total: next } }));
  };

  const markRead = useCallback(
    async (tid: string, otherUserId: string) => {
      try {
        await chatService.markRead(tid);
        setContacts((prev) => {
          const was = prev.find((c) => c.userId === otherUserId)?.unread ?? 0;
          if (was > 0) {
            setTotalUnread((t) => {
              const next = Math.max(0, t - was);
              publishTotal(next);
              return next;
            });
          }
          return prev.map((c) => (c.userId === otherUserId ? { ...c, unread: 0 } : c));
        });
      } catch {
        // próximo poll corrige
      }
    },
    []
  );

  const markGroupRead = useCallback(async (tid: string) => {
    try {
      await chatService.markRead(tid);
      setGroups((prev) => {
        const was = prev.find((g) => g.threadId === tid)?.unread ?? 0;
        if (was > 0) {
          setTotalUnread((t) => {
            const next = Math.max(0, t - was);
            publishTotal(next);
            return next;
          });
        }
        return prev.map((g) => (g.threadId === tid ? { ...g, unread: 0 } : g));
      });
    } catch {
      // próximo poll corrige
    }
  }, []);

  const loadThread = useCallback(
    async (conv: { kind: "person" | "group"; key: string }) => {
      setLoadingThread(true);
      try {
        const data = conv.kind === "group" ? await chatService.groupMessages(conv.key) : await chatService.messages(conv.key);
        setMessages(mergeMessages([], data.messages));
        watermarkRef.current = maxSentAt(data.messages);
        setThreadId(data.threadId);
        setOtherReadAt(data.otherLastReadAt);
        if (data.threadId) {
          if (conv.kind === "group") markGroupRead(data.threadId);
          else markRead(data.threadId, conv.key);
        }
      } catch {
        setSendError("Não foi possível carregar a conversa.");
      } finally {
        setLoadingThread(false);
      }
    },
    [markRead, markGroupRead]
  );

  // Polling da conversa aberta: só as mensagens depois da última que temos
  const convKind = conversation?.kind ?? null;
  const convKey = conversation?.key ?? null;
  useEffect(() => {
    if (!visible || !convKind || !convKey) return;
    const timer = window.setInterval(async () => {
      try {
        const data = convKind === "group" ? await chatService.groupMessages(convKey, watermarkRef.current) : await chatService.messages(convKey, watermarkRef.current);
        if (data.otherLastReadAt) setOtherReadAt(data.otherLastReadAt);
        if (data.deletedIds && data.deletedIds.length > 0) {
          const gone = new Set(data.deletedIds);
          setMessages((prev) => prev.filter((m) => !gone.has(m.id)));
        }
        const fresh = data.messages.filter((m) => !knownIdsRef.current.has(m.id));
        if (fresh.length > 0) {
          const newest = maxSentAt(fresh);
          if (newest && (!watermarkRef.current || newest > watermarkRef.current)) watermarkRef.current = newest;
          setMessages((prev) => mergeMessages(prev, fresh));
          if (data.threadId) {
            setThreadId(data.threadId);
            if (fresh.some((m) => !m.mine)) {
              if (!muted) playIncomingTone();
              if (convKind === "group") markGroupRead(data.threadId);
              else markRead(data.threadId, convKey);
            }
          }
        }
      } catch {
        // silencioso
      }
    }, THREAD_POLL_MS);
    return () => window.clearInterval(timer);
  }, [visible, convKind, convKey, muted, markRead, markGroupRead]);

  useEffect(() => {
    if (!conversation) return;
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, convKey, sending]);

  useEffect(() => {
    const pending = messages.filter((m) => m.attachment?.hasThumb && !thumbs[m.id]);
    if (pending.length === 0) return;
    let active = true;
    Promise.all(
      pending.map(async (m) => {
        try {
          return [m.id, URL.createObjectURL(await chatService.attachment(m.id, true))] as const;
        } catch {
          return null;
        }
      })
    ).then((pairs) => {
      if (!active) return;
      setThumbs((prev) => {
        const next = { ...prev };
        pairs.forEach((p) => {
          if (p) next[p[0]] = p[1];
        });
        return next;
      });
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const sendFile = async (file: File) => {
    if (!conversation || sending) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setSendError("O arquivo tem no máximo 10 MB.");
      return;
    }
    setSendError(null);
    setSending(true);
    const caption = draft.trim();
    try {
      const sent =
        conversation.kind === "group"
          ? await chatService.sendGroupAttachment(conversation.key, file, caption || undefined)
          : await chatService.sendAttachment(conversation.key, file, caption || undefined);
      setDraft("");
      setMessages((prev) => mergeMessages(prev, [sent]));
      if (conversation.kind === "group") {
        setGroups((prev) =>
          prev.map((g) => (g.threadId === conversation.key ? { ...g, lastMessage: `Arquivo: ${file.name}`, lastMessageByName: null, lastMessageAt: sent.sentAt, lastMessageMine: true } : g))
        );
      } else {
        setContacts((prev) =>
          prev.map((c) =>
            c.userId === conversation.key
              ? { ...c, lastMessage: `Arquivo: ${file.name}`, lastMessageAt: sent.sentAt, lastMessageMine: true, lastMessageRead: false }
              : c
          )
        );
      }
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { description?: string } } })?.response?.data;
      setSendError(data?.description || "Não foi possível enviar o arquivo.");
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Colar imagem da área de transferência envia como anexo
  const onPaste = (e: React.ClipboardEvent) => {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith("image/"));
    if (!item) return;
    const file = item.getAsFile();
    if (!file) return;
    e.preventDefault();
    const ext = file.type.split("/")[1]?.replace("jpeg", "jpg") || "png";
    sendFile(new File([file], `imagem-colada-${Date.now()}.${ext}`, { type: file.type }));
  };

  const openPerson = (c: ChatContact) => {
    setSelected({ kind: "person", id: c.userId });
    setMessages([]);
    watermarkRef.current = null;
    setThreadId(c.threadId);
    setOtherReadAt(null);
    setSendError(null);
    setDraft("");
    loadThread({ kind: "person", key: c.userId });
    window.setTimeout(() => draftRef.current?.focus(), 50);
  };

  const openGroup = (g: ChatGroup) => {
    setSelected({ kind: "group", id: g.threadId });
    setMessages([]);
    watermarkRef.current = null;
    setThreadId(g.threadId);
    setOtherReadAt(null);
    setSendError(null);
    setDraft("");
    setMembersOpen(false);
    setGroupMembers([]);
    chatService.group(g.threadId).then((d) => setGroupMembers(d.members)).catch(() => undefined);
    loadThread({ kind: "group", key: g.threadId });
    window.setTimeout(() => draftRef.current?.focus(), 50);
  };

  // Grupo criado agora: entra na lista e já abre
  const onGroupCreated = (g: ChatGroup) => {
    setNewGroupOpen(false);
    setGroups((prev) => [g, ...prev.filter((x) => x.threadId !== g.threadId)]);
    setFilter((f) => (f === "agents" || f === "people" ? "all" : f));
    openGroup(g);
  };

  // Saí do grupo (ou ele foi arquivado): some da lista e volta para os contatos
  const onLeftGroup = (threadId: string) => {
    setMembersOpen(false);
    setGroups((prev) => prev.filter((g) => g.threadId !== threadId));
    setSelected(null);
    setMessages([]);
  };

  // Renomeado ou com outra quantidade de membros: a lista, o cabeçalho e as menções acompanham
  const onGroupChanged = (g: ChatGroup) => {
    setGroups((prev) => prev.map((x) => (x.threadId === g.threadId ? { ...x, title: g.title, memberCount: g.memberCount, myRole: g.myRole } : x)));
    chatService.group(g.threadId).then((d) => setGroupMembers(d.members)).catch(() => undefined);
  };

  // ---------- Menção com @ (só em grupo) ----------
  const myUserId = user?.userId ?? "";
  const mentionRegex = useMemo(() => mentionRegexFor(groupMembers), [groupMembers]);
  const renderContent = (text: string, mine: boolean): React.ReactNode => {
    if (!mentionRegex) return text;
    const parts: React.ReactNode[] = [];
    let last = 0;
    mentionRegex.lastIndex = 0;
    let hit: RegExpExecArray | null;
    while ((hit = mentionRegex.exec(text)) !== null) {
      if (hit.index > last) parts.push(text.slice(last, hit.index));
      // Menção: só negrito, sem fundo nem outra cor (decisão do Hilton, 26/10) — a conversa fica com uma família de cores só
      parts.push(
        <Box key={hit.index} component="span" sx={{ fontWeight: 700 }}>
          {hit[0]}
        </Box>
      );
      last = hit.index + hit[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };
  const mentionMatch = conversation?.kind === "group" && !mentionDismissed ? /(?:^|\s)@([^\s@]*)$/.exec(draft) : null;
  const mentionQuery = mentionMatch ? mentionMatch[1].toLowerCase() : null;
  const mentionOptions = mentionQuery !== null ? groupMembers.filter((m) => !m.isMe && m.name.toLowerCase().includes(mentionQuery)).slice(0, 6) : [];
  const pickMention = (m: ChatGroupMember) => {
    setDraft((prev) => prev.replace(/@([^\s@]*)$/, `@${m.name} `));
    window.setTimeout(() => draftRef.current?.focus(), 0);
  };

  const openReaders = async (m: ChatMessage) => {
    if (!selectedGroup) return;
    setReadersFor(m);
    setReaders(null);
    try {
      setReaders(await chatService.groupReaders(selectedGroup.threadId, m.id));
    } catch {
      setReaders({ read: [], pending: [] });
    }
  };

  const openAgent = (a: MyAgent) => setSelected({ kind: "agent", id: a.id });

  const openSupport = (a: SupportAgentInfo) => setSelected({ kind: "support", id: a.id });

  const back = () => {
    if (selected?.kind === "agent" && agentRef.current?.back()) return;
    if (selected?.kind === "support" && supportRef.current?.back()) return;
    setSelected(null);
    loadContacts();
  };

  const dismiss = () => {
    sessionStorage.setItem(SESSION_HIDE_KEY, "1");
    setDismissed(true);
    setOpen(false);
  };

  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    setChatMuted(next);
  };

  const sendDirect = async () => {
    const text = draft.trim();
    if (!text || !conversation || sending) return;
    setDraft("");
    setSendError(null);
    setSending(true);
    try {
      const sent = conversation.kind === "group" ? await chatService.sendToGroup(conversation.key, text) : await chatService.send(conversation.key, text);
      setMessages((prev) => mergeMessages(prev, [sent]));
      if (conversation.kind === "group") {
        setGroups((prev) =>
          prev.map((g) => (g.threadId === conversation.key ? { ...g, lastMessage: sent.content, lastMessageByName: null, lastMessageAt: sent.sentAt, lastMessageMine: true } : g))
        );
      } else {
        setContacts((prev) =>
          prev.map((c) =>
            c.userId === conversation.key
              ? { ...c, lastMessage: sent.content, lastMessageAt: sent.sentAt, lastMessageMine: true, lastMessageRead: false }
              : c
          )
        );
        if (!threadId) {
          const data = await chatService.messages(conversation.key);
          setThreadId(data.threadId);
        }
      }
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { description?: string } } })?.response?.data;
      setSendError(data?.description || "Não foi possível enviar a mensagem.");
      setDraft(text);
    } finally {
      setSending(false);
      draftRef.current?.focus();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await chatService.deleteMessage(deleteTarget.id);
      setMessages((prev) => prev.filter((m) => m.id !== deleteTarget.id));
      setDeleteTarget(null);
      loadContacts();
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { description?: string } } })?.response?.data;
      setSendError(data?.description || "Não foi possível apagar a mensagem.");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const filteredPeople = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => !q || c.name.toLowerCase().includes(q) || (c.role ?? "").toLowerCase().includes(q));
  }, [contacts, search]);

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agents.filter((a) => !q || a.displayName.toLowerCase().includes(q));
  }, [agents, search]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => !q || g.title.toLowerCase().includes(q));
  }, [groups, search]);

  useEffect(() => {
    if (!open || !maximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMaximized(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, maximized]);

  const coversScreen = fullScreen || maximized;
  // Encaixado na página, o espaço é da área: duas colunas sempre que a tela não for de celular
  const twoPane = (docked || expanded || maximized) && !fullScreen;
  // Tela cheia ancora nos quatro cantos: 100vw inclui a barra de rolagem da página e empurrava a borda esquerda para fora.
  const panelWidth = coversScreen ? "auto" : twoPane ? "min(960px, 94vw)" : "420px";
  const panelHeight = coversScreen ? "auto" : twoPane ? "min(720px, 88vh)" : "min(660px, 86vh)";
  const showList = twoPane || selected === null;
  const showThread = twoPane || selected !== null;

  if (!canLoad) return null;

  // ---------- Cabeçalho ----------
  const supportInfo = selected?.kind === "support" ? supportAgents.find((a) => a.id === selected.id) ?? null : null;
  const supportSelected = !!supportInfo;

  // Encaixado na página o cabeçalho é branco, como os painéis do sistema; flutuante, mantém o degradê
  const headFg = docked ? colors.textSecondary : "#fff";
  const headAvatarSx = docked
    ? { bgcolor: colors.primary, color: "#fff" }
    : { bgcolor: "rgba(255,255,255,0.25)", color: "#fff" };
  // Encaixado, segue o cabeçalho das demais áreas de Assistentes (ícone de 48px)
  const headSize = docked ? 48 : 36;
  const headIcon = docked ? 24 : 22;

  const headerAvatar = () => {
    if (supportSelected) {
      return (
        <Avatar sx={{ width: headSize, height: headSize, bgcolor: docked ? colors.primarySoft : "#fff", color: docked ? colors.primaryText : colors.primary }}>
          <SupportAgent sx={{ fontSize: headIcon }} />
        </Avatar>
      );
    }
    if (selectedAgent) {
      return (
        <Avatar src={avatars[selectedAgent.id]} sx={{ width: headSize, height: headSize, ...headAvatarSx, fontSize: "0.8rem" }}>
          {initials(selectedAgent.displayName)}
        </Avatar>
      );
    }
    if (selectedContact?.isSystem) {
      return (
        <Avatar src={sininhoAvatar} alt="Sininho" sx={{ width: headSize, height: headSize, bgcolor: "#f3ede6" }} />
      );
    }
    if (selectedGroup) {
      return (
        <Avatar sx={{ width: headSize, height: headSize, ...(docked ? { bgcolor: groupColor(selectedGroup.threadId), color: "#fff" } : headAvatarSx) }}>
          <Groups sx={{ fontSize: headIcon }} />
        </Avatar>
      );
    }
    if (selectedContact) {
      return (
        <Avatar src={photos[selectedContact.userId]} sx={{ width: headSize, height: headSize, bgcolor: personColor(selectedContact.userId), color: "#fff", fontSize: "0.8rem", fontWeight: 600 }}>
          {initials(selectedContact.name)}
        </Avatar>
      );
    }
    return (
      <Box sx={{ width: headSize, height: headSize, borderRadius: "50%", bgcolor: docked ? colors.primarySoft : "rgba(255,255,255,0.2)", color: docked ? colors.primaryText : "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Forum sx={{ fontSize: headIcon }} />
      </Box>
    );
  };

  const headerTitle = supportSelected ? supportInfo!.displayName : selectedAgent?.displayName ?? selectedContact?.name ?? selectedGroup?.title ?? "Chat";
  const headerSubtitle = supportSelected
    ? supportInfo!.subtitle || "Suporte do sistema Clinic4Us"
    : selectedAgent
    ? "Assistente de IA"
    : selectedContact?.isSystem
      ? "Avisos automáticos · sem resposta"
      : selectedContact
      ? `${selectedContact.role ?? "Equipe"} · ${selectedContact.online ? "online" : "offline"}`
      : selectedGroup
      ? `${selectedGroup.memberCount} ${selectedGroup.memberCount === 1 ? "membro" : "membros"} · ${selectedGroup.myRole === "admin" ? "você administra" : "grupo"}`
      : user?.clinicName || "Equipe";

  // ---------- Linhas da lista ----------
  // Linha da lista no padrão da coluna dos Assistentes (kit): mesmo respiro do histórico da Agenda e do Livro-caixa
  const rowSx = (active: boolean) => ({ ...listRowSx(active), cursor: "pointer" });

  const PersonRow: React.FC<{ c: ChatContact }> = ({ c }) => {
    const active = selected?.kind === "person" && selected.id === c.userId;
    return (
      <Box onClick={() => openPerson(c)} sx={rowSx(active)}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          variant="dot"
          invisible={!c.online}
          sx={{ "& .MuiBadge-dot": { bgcolor: "#2ecc71", border: "2px solid #fff", width: 12, height: 12, borderRadius: "50%" } }}
        >
          {c.isSystem ? (
            <Avatar src={sininhoAvatar} alt="Sininho" sx={{ width: 46, height: 46, bgcolor: "#f3ede6" }} />
          ) : (
            <Avatar src={photos[c.userId]} sx={{ width: 46, height: 46, bgcolor: personColor(c.userId), color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>
              {initials(c.name)}
            </Avatar>
          )}
        </Badge>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: c.unread > 0 ? 700 : 600, fontSize: "0.92rem", color: colors.textPrimary }} noWrap>
              {c.name}
            </Typography>
            {c.lastMessageAt && (
              <Typography sx={{ fontSize: "0.72rem", color: c.unread > 0 ? colors.primary : colors.textSecondary, flexShrink: 0, fontWeight: c.unread > 0 ? 600 : 400 }}>
                {listTime(c.lastMessageAt)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.2 }}>
            <Typography sx={{ fontSize: "0.8rem", color: c.unread > 0 ? colors.textPrimary : colors.textSecondary, display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }} noWrap>
              {c.lastMessage && c.lastMessageMine && (c.lastMessageRead ? <DoneAll sx={{ fontSize: 15, color: "#4fc3f7", flexShrink: 0 }} /> : <Done sx={{ fontSize: 15, color: colors.textSecondary, flexShrink: 0 }} />)}
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.lastMessage ?? c.role ?? "Equipe"}</Box>
            </Typography>
            {c.unread > 0 && (
              <Box sx={{ minWidth: 20, height: 20, px: 0.6, borderRadius: 10, bgcolor: colors.primary, color: "#fff", fontSize: "0.7rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {c.unread}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const GroupRow: React.FC<{ g: ChatGroup }> = ({ g }) => {
    const active = selected?.kind === "group" && selected.id === g.threadId;
    const preview = g.lastMessage
      ? `${g.lastMessageMine ? "Você" : g.lastMessageByName ?? ""}${g.lastMessageMine || g.lastMessageByName ? ": " : ""}${g.lastMessage}`
      : `${g.memberCount} ${g.memberCount === 1 ? "membro" : "membros"}`;
    return (
      <Box onClick={() => openGroup(g)} sx={rowSx(active)}>
        <Avatar sx={{ width: 46, height: 46, bgcolor: groupColor(g.threadId), color: "#fff" }}>
          <Groups sx={{ fontSize: 26 }} />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: g.unread > 0 ? 700 : 600, fontSize: "0.92rem", color: colors.textPrimary }} noWrap>
              {g.title}
            </Typography>
            {g.lastMessageAt && (
              <Typography sx={{ fontSize: "0.72rem", color: g.unread > 0 ? colors.primary : colors.textSecondary, flexShrink: 0, fontWeight: g.unread > 0 ? 600 : 400 }}>
                {listTime(g.lastMessageAt)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.2 }}>
            <Typography sx={{ fontSize: "0.8rem", color: g.unread > 0 ? colors.textPrimary : colors.textSecondary, minWidth: 0 }} noWrap>
              {preview}
            </Typography>
            {(g.mentionedUnread ?? 0) > 0 && (
              <Tooltip title="Alguém mencionou você">
                <Box sx={{ minWidth: 20, height: 20, px: 0.6, borderRadius: 10, bgcolor: "#e6f7f9", color: colors.primaryText, fontSize: "0.72rem", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-label="Alguém mencionou você">
                  @
                </Box>
              </Tooltip>
            )}
            {g.unread > 0 && (
              <Box sx={{ minWidth: 20, height: 20, px: 0.6, borderRadius: 10, bgcolor: colors.primary, color: "#fff", fontSize: "0.7rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {g.unread}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const AgentRow: React.FC<{ a: MyAgent }> = ({ a }) => {
    const active = selected?.kind === "agent" && selected.id === a.id;
    return (
      <Box onClick={() => openAgent(a)} sx={rowSx(active)}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={<AutoAwesome sx={{ fontSize: 10, color: "#fff" }} />}
          sx={{ "& .MuiBadge-badge": { bgcolor: "#7048E8", border: "2px solid #fff", width: 18, height: 18, minWidth: 18, borderRadius: "50%", p: 0 } }}
        >
          <Avatar src={avatars[a.id]} sx={{ width: 46, height: 46, bgcolor: colors.primary, color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>
            {initials(a.displayName)}
          </Avatar>
        </Badge>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.92rem", color: colors.textPrimary }} noWrap>
              {a.displayName}
            </Typography>
            <Chip label="IA" size="small" sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#f3f0ff", color: "#5f3dc4", "& .MuiChip-label": { px: 0.75 } }} />
          </Box>
          <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mt: 0.2 }} noWrap>
            Assistente de IA · sempre disponível
          </Typography>
        </Box>
      </Box>
    );
  };

  const term = search.trim().toLowerCase();
  const visibleSupportAgents =
    filter === "people"
      ? []
      : supportAgents.filter(
          (a) => !term || a.displayName.toLowerCase().includes(term) || (a.isSupport && "suporte ajuda".includes(term))
        );

  // Agentes da plataforma (o Suporte Clinic4Us e os que a equipe Clinic4Us liberar para a clínica).
  const SupportRow: React.FC<{ a: SupportAgentInfo }> = ({ a }) => (
    <Box onClick={() => openSupport(a)} sx={rowSx(selected?.kind === "support" && selected.id === a.id)}>
      <Badge
        overlap="circular"
        badgeContent={a.isSupport ? supportUnread : 0}
        color="error"
        sx={{ "& .MuiBadge-badge": { fontWeight: 700, border: "2px solid #fff" } }}
      >
        <Avatar sx={{ width: 46, height: 46, bgcolor: "#e6f7f9", color: colors.primary }}>
          <SupportAgent sx={{ fontSize: 26 }} />
        </Avatar>
      </Badge>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.92rem", color: colors.textPrimary }} noWrap>
            {a.displayName}
          </Typography>
          <Chip label="IA" size="small" sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#f3f0ff", color: "#5f3dc4", "& .MuiChip-label": { px: 0.75 } }} />
        </Box>
        <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mt: 0.2 }} noWrap>
          {a.subtitle || "Suporte do sistema Clinic4Us"}
        </Typography>
      </Box>
    </Box>
  );

  const sectionTitle = (label: string) => (
    <Typography sx={{ px: 1.75, pt: 1.25, pb: 0.5, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.08em", color: colors.textSecondary, textTransform: "uppercase" }}>
      {label}
    </Typography>
  );

  // ---------- Lista de contatos ----------
  const contactList = (
    <Box sx={twoPane ? { ...listColumnSx, height: "100%" } : { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%", bgcolor: "#fff" }}>
      <Box sx={{ ...listHeaderSx, gap: 0 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar pessoa, grupo ou assistente"
          size="small"
          fullWidth
          inputProps={{ maxLength: 60, style: { fontSize: "0.88rem" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: colors.textSecondary }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "22px", bgcolor: "#f1f3f5", "& fieldset": { border: "none" } } }}
        />
        <Box sx={{ display: "flex", gap: 0.75, mt: 1.25 }}>
          {([
            ["all", "Todos"],
            ["people", "Pessoas"],
            ["groups", "Grupos"],
            ["agents", "Assistentes"],
          ] as [Filter, string][]).map(([key, label]) => (
            <Chip
              key={key}
              label={label}
              size="small"
              clickable
              onClick={() => setFilter(key)}
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                bgcolor: filter === key ? colors.primary : "#f1f3f5",
                color: filter === key ? "#fff" : colors.textSecondary,
                "&:hover": { bgcolor: filter === key ? "#029AAB" : "#e9ecef" },
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {visibleSupportAgents.length > 0 && (
          <>
            {sectionTitle("Suporte do sistema")}
            {visibleSupportAgents.map((a) => (
              <SupportRow key={a.id} a={a} />
            ))}
          </>
        )}
        {filter !== "people" && filter !== "groups" && filteredAgents.length > 0 && (
          <>
            {sectionTitle("Assistentes")}
            {filteredAgents.map((a) => (
              <AgentRow key={a.id} a={a} />
            ))}
          </>
        )}
        {(filter === "all" || filter === "groups") && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
              {sectionTitle("Grupos")}
              <Button
                size="small"
                startIcon={<GroupAdd sx={{ fontSize: 16 }} />}
                onClick={() => setNewGroupOpen(true)}
                sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, color: colors.primary, minWidth: 0, py: 0.25 }}
              >
                Novo grupo
              </Button>
            </Box>
            {contactsLoaded && filteredGroups.length === 0 && (
              <Typography sx={{ px: 2, pb: 1.5, fontSize: "0.8rem", color: colors.textSecondary }}>
                {filter === "groups" ? "Você ainda não está em nenhum grupo. Crie um em Novo grupo." : "Nenhum grupo ainda."}
              </Typography>
            )}
            {filteredGroups.map((g) => (
              <GroupRow key={g.threadId} g={g} />
            ))}
          </>
        )}
        {filter !== "agents" && filter !== "groups" && (
          <>
            {sectionTitle("Equipe")}
            {!contactsLoaded ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={20} sx={{ color: colors.primary }} />
              </Box>
            ) : filteredPeople.length === 0 ? (
              <Typography sx={{ px: 2, py: 3, textAlign: "center", fontSize: "0.85rem", color: colors.textSecondary }}>Ninguém encontrado.</Typography>
            ) : (
              filteredPeople.map((c) => <PersonRow key={c.userId} c={c} />)
            )}
          </>
        )}
        {filter === "agents" && filteredAgents.length === 0 && (
          <Typography sx={{ px: 2, py: 3, textAlign: "center", fontSize: "0.85rem", color: colors.textSecondary }}>Nenhum assistente atribuído a você.</Typography>
        )}
      </Box>
      {!docked && (
        <Box sx={{ px: 1.5, py: 0.75, borderTop: "1px solid #f1f3f5", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Typography onClick={dismiss} sx={{ fontSize: "0.68rem", color: colors.textSecondary, cursor: "pointer", textDecoration: "underline" }}>
            Ocultar balão nesta sessão
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ---------- Conversa direta ou grupo ----------
  const directThread = conversation && (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
      <AuditNotice clinicName={user?.clinicName} />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.5,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          backgroundColor: colors.white,
        }}
      >
        {loadingThread && messages.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} sx={{ color: colors.primary }} />
          </Box>
        )}
        {!loadingThread && messages.length === 0 && conversation.isSystem && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, mt: 3, px: 2, textAlign: "center" }}>
            <Box component="img" src={sininhoFull} alt="Sininho" sx={{ width: 110, maxWidth: "40%", height: "auto" }} />
            <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, maxWidth: 280 }}>
              Nenhum aviso ainda. Quando um paciente chegar para você, o Sininho avisa por aqui.
            </Typography>
          </Box>
        )}
        {!loadingThread && messages.length === 0 && !conversation.isSystem && (
          <Typography sx={{ textAlign: "center", fontSize: "0.85rem", color: colors.textSecondary, mt: 3 }}>
            {conversation.kind === "group"
              ? `Nenhuma mensagem ainda. Comece a conversa do grupo ${conversation.name}.`
              : `Nenhuma mensagem ainda. Diga oi para ${conversation.name.split(" ")[0]}.`}
          </Typography>
        )}
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const newDay = !prev || !isSameDay(parse(prev.sentAt), parse(m.sentAt));
          const sameAuthorAsPrev = prev && prev.mine === m.mine && (prev.senderId ?? null) === (m.senderId ?? null) && !newDay;
          // Na direta, "lido até" do outro; em grupo, o menor "lido até" dos outros membros (tique duplo = todos leram)
          const read = m.mine && !!otherReadAt && parse(otherReadAt).getTime() >= parse(m.sentAt).getTime();
          const showSender = conversation.kind === "group" && !m.mine && !sameAuthorAsPrev;
          const mentionsMe = conversation.kind === "group" && !m.mine && !!m.mentions?.includes(myUserId);
          const dayChip = newDay && (
            <Box sx={{ alignSelf: "center", my: 1, px: 1.25, py: 0.3, borderRadius: 10, bgcolor: colors.background, color: colors.textSecondary }}>
              <Typography sx={{ fontSize: "0.7rem", color: colors.textSecondary, fontWeight: 600 }}>{dayLabel(m.sentAt)}</Typography>
            </Box>
          );
          // Mensagem de sistema do grupo ("Ana adicionou Bia"): centralizada, sem autor nem tique
          if (m.action?.type === "group-event") {
            return (
              <React.Fragment key={m.id}>
                {dayChip}
                <Box sx={{ alignSelf: "center", my: 0.5, px: 1.25, py: 0.35, borderRadius: 10, bgcolor: colors.background, maxWidth: "90%" }}>
                  <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary, textAlign: "center" }}>{m.content} · {hourOf(m.sentAt)}</Typography>
                </Box>
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={m.id}>
              {dayChip}
              <Box
                sx={{
                  alignSelf: m.mine ? "flex-end" : "flex-start",
                  maxWidth: "78%",
                  // Troca de autor abre mais espaço; o contorno da menção precisa de folga para não colar no balão de cima
                  mt: mentionsMe ? 1.25 : sameAuthorAsPrev ? 0.75 : 1.5,
                  mb: mentionsMe ? 0.75 : 0,
                  px: 1.5,
                  py: 0.85,
                  bgcolor: m.mine ? colors.primary : "#f1f3f5",
                  color: m.mine ? "#fff" : colors.textPrimary,
                  borderRadius: m.mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                  // Mensagem que me menciona: barra fina à esquerda na cor primária, sem cor nova na tela
                  borderLeft: mentionsMe ? `3px solid ${colors.primary}` : "none",
                  fontSize: "0.9rem",
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {showSender && (
                  <Typography sx={{ fontSize: "0.64rem", fontWeight: 600, color: colors.textSecondary, lineHeight: 1.2, mb: 0.35 }} noWrap>
                    {m.senderName ?? "Usuário"}
                  </Typography>
                )}
                {m.attachment && (
                  <Box onClick={() => openAttachment(m)} sx={{ cursor: "pointer", mb: m.content ? 0.75 : 0 }}>
                    {m.attachment.isImage && thumbs[m.id] ? (
                      <Box component="img" src={thumbs[m.id]} alt={m.attachment.name} sx={{ display: "block", width: 220, maxWidth: "100%", borderRadius: "10px" }} />
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: "10px", bgcolor: m.mine ? "rgba(255,255,255,0.18)" : colors.white, minWidth: 180 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: m.mine ? "rgba(255,255,255,0.25)" : "#e3e7ea", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {fileIcon(m.attachment)}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, lineHeight: 1.25 }} noWrap>{m.attachment.name}</Typography>
                          <Typography sx={{ fontSize: "0.7rem", opacity: 0.85 }}>{formatSize(m.attachment.size)}</Typography>
                        </Box>
                        <Download sx={{ fontSize: 18, opacity: 0.8 }} />
                      </Box>
                    )}
                  </Box>
                )}
                {m.content && <Box sx={{ whiteSpace: "pre-wrap" }}>{conversation.kind === "group" ? renderContent(m.content, m.mine) : m.content}</Box>}
                {m.action?.type === "open-patient" && m.action.patientId && canOpenPatient && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                    // Abrir a ficha fecha o chat: o painel ficava por cima do cadastro (pedido do Hilton, 28/09)
                    onClick={() => { setOpen(false); navigateTo("patient-register", { id: m.action!.patientId! }); }}
                    sx={{ mt: 1, textTransform: "none", borderRadius: "16px", borderColor: colors.primary, color: colors.primary, fontSize: "0.8rem", py: 0.25 }}
                  >
                    Abrir cadastro
                  </Button>
                )}
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.4, mt: 0.25, opacity: 0.85 }}>
                  {m.mine && (
                    <Tooltip title="Apagar mensagem">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(m);
                        }}
                        sx={{ p: 0.25, mr: 0.5, color: "rgba(255,255,255,0.75)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.15)" } }}
                        aria-label="Apagar mensagem"
                      >
                        <DeleteOutline sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Typography sx={{ fontSize: "0.66rem" }}>{hourOf(m.sentAt)}</Typography>
                  {m.mine && conversation.kind === "group" ? (
                    <Tooltip title="Quem já leu">
                      <Box component="button" type="button" onClick={(e: React.MouseEvent) => { e.stopPropagation(); openReaders(m); }} aria-label="Quem já leu" sx={{ display: "inline-flex", alignItems: "center", p: 0.25, m: -0.25, border: 0, bgcolor: "transparent", color: "inherit", cursor: "pointer", borderRadius: "8px", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
                        {read ? <DoneAll sx={{ fontSize: 14, color: "#d0f4ff" }} /> : <Done sx={{ fontSize: 14 }} />}
                      </Box>
                    </Tooltip>
                  ) : (
                    m.mine && (read ? <DoneAll sx={{ fontSize: 14, color: "#d0f4ff" }} /> : <Done sx={{ fontSize: 14 }} />)
                  )}
                </Box>
              </Box>
            </React.Fragment>
          );
        })}
        {sendError && (
          <Typography sx={{ alignSelf: "center", fontSize: "0.75rem", color: colors.error, mt: 1 }}>{sendError}</Typography>
        )}
        <div ref={threadEndRef} />
      </Box>
      {conversation.kind === "person" && !conversation.online && !conversation.isSystem && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            py: 0.85,
            borderTop: "1px solid #ffe0a3",
            bgcolor: "#fff8e6",
            color: "#8a6100",
          }}
        >
          <InfoOutlined sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}>
            <strong>{conversation.name.split(" ")[0]}</strong> está offline agora. Pode escrever
            mesmo assim: a mensagem fica guardada e aparece assim que a pessoa entrar no sistema.
          </Typography>
        </Box>
      )}
      {mentionOptions.length > 0 && (
        <Paper elevation={3} sx={{ mx: 1, mb: 0.5, borderRadius: "10px", overflow: "hidden" }} role="listbox" aria-label="Mencionar pessoa">
          {mentionOptions.map((m) => (
            <Box key={m.userId} role="option" aria-selected={false} onClick={() => pickMention(m)} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.6, cursor: "pointer", "&:hover": { bgcolor: "#f6f7f9" } }}>
              <Avatar src={photos[m.userId]} sx={{ width: 28, height: 28, bgcolor: personColor(m.userId), color: "#fff", fontSize: "0.7rem", fontWeight: 600 }}>{initials(m.name)}</Avatar>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textPrimary }} noWrap>{m.name}</Typography>
              <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary }} noWrap>{m.role ?? "Equipe"}</Typography>
            </Box>
          ))}
          <Typography sx={{ px: 1.25, py: 0.4, fontSize: "0.68rem", color: colors.textSecondary, borderTop: "1px solid #f1f3f5" }}>Enter escolhe o primeiro · Esc fecha</Typography>
        </Paper>
      )}
      {conversation.isSystem ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.5, py: 1.1, borderTop: "1px solid #e9ecef", bgcolor: "#fff", color: colors.textSecondary }}>
          <InfoOutlined sx={{ fontSize: 16, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}>
            Avisos automáticos da agenda. Esta conversa não aceita resposta.
          </Typography>
        </Box>
      ) : (
      // Caixa de mensagem do kit (linguagem única com Ani, Suporte e áreas dos Assistentes)
      <Box sx={{ flexShrink: 0, px: 1.5, pt: 1, pb: 1.25, bgcolor: colors.white }}>
      <ComposerFrame isMobile={fullScreen}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILES}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) sendFile(file);
          }}
        />
        <ComposerIconButton label="Anexar arquivo" title="Anexar arquivo (imagem, PDF ou documento, até 10 MB). O texto digitado vai como legenda."
          onClick={() => fileInputRef.current?.click()} isMobile={fullScreen} disabled={sending}>
          <AttachFile />
        </ComposerIconButton>
        <InputBase
          value={draft}
          inputRef={draftRef}
          onPaste={onPaste}
          onChange={(e) => {
            setDraft(e.target.value);
            setMentionDismissed(false);
          }}
          onKeyDown={(e) => {
            if (mentionOptions.length > 0 && e.key === "Escape") {
              e.preventDefault();
              setMentionDismissed(true);
              return;
            }
            if (mentionOptions.length > 0 && (e.key === "Enter" || e.key === "Tab") && !e.shiftKey) {
              e.preventDefault();
              pickMention(mentionOptions[0]);
              return;
            }
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendDirect();
            }
          }}
          placeholder={conversation.kind === "group" ? `Mensagem para o grupo ${conversation.name}…` : `Mensagem para ${conversation.name.split(" ")[0]}…`}
          fullWidth
          multiline
          minRows={fullScreen ? 1 : 2}
          maxRows={6}
          disabled={sending}
          inputProps={{ maxLength: 4000, "aria-label": "Mensagem" }}
          sx={{ fontSize: "0.9375rem", lineHeight: 1.45, color: colors.textStrong, py: 0.5, "& textarea::placeholder": { color: colors.textSecondary, opacity: 1 } }}
        />
        <SendButton onClick={sendDirect} disabled={sending || !draft.trim()} isMobile={fullScreen} />
      </ComposerFrame>
      </Box>
      )}
    </Box>
  );

  const emptyPane = (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, bgcolor: colors.white, color: colors.textSecondary, px: 3, textAlign: "center" }}>
      <Box sx={{ width: 84, height: 84, borderRadius: "50%", bgcolor: "#eef7f8", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChatBubbleOutline sx={{ fontSize: 38, color: colors.primary }} />
      </Box>
      <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>Chat da {user?.clinicName || "clínica"}</Typography>
      <Typography sx={{ fontSize: "0.85rem", maxWidth: 320 }}>
        Fale com a equipe ou peça ajuda a um assistente de IA. Escolha um contato à esquerda para começar.
      </Typography>
    </Box>
  );

  const threadPane = supportSelected ? (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
      <SupportConversation key={supportInfo!.id} ref={supportRef} info={supportInfo!} currentPage={currentPage} onUnreadTickets={setSupportUnread} />
    </Box>
  ) : selectedAgent ? (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
      <AgentConversation
        ref={agentRef}
        agent={selectedAgent}
        currentPage={currentPage}
        clinicName={user?.clinicName || ""}
        onViewChange={setAgentView}
      />
    </Box>
  ) : conversation ? (
    directThread
  ) : (
    emptyPane
  );

  const headerBack = selected !== null && (!twoPane || (selectedAgent && agentView === "chat")) && (
    <IconButton size="small" onClick={back} sx={{ color: headFg, ml: -0.5 }} title={selectedAgent && agentView === "chat" ? "Histórico de conversas" : "Voltar"} aria-label="Voltar">
      <ArrowBack fontSize="small" />
    </IconButton>
  );
  const headerActions = (
    <>
      {selectedAgent && (agentView === "chat" || twoPane) && (
        <IconButton size="small" onClick={() => agentRef.current?.startNew()} sx={{ color: headFg }} title="Nova conversa" aria-label="Nova conversa">
          <Add fontSize="small" />
        </IconButton>
      )}
      {supportSelected && (
        <IconButton size="small" onClick={() => supportRef.current?.startNew()} sx={{ color: headFg }} title="Nova conversa" aria-label="Nova conversa">
          <Add fontSize="small" />
        </IconButton>
      )}
      {selectedGroup && (
        <IconButton size="small" onClick={() => setMembersOpen(true)} sx={{ color: headFg }} title="Membros do grupo" aria-label="Membros do grupo">
          <People fontSize="small" />
        </IconButton>
      )}
      <IconButton size="small" onClick={toggleMuted} sx={{ color: headFg, opacity: muted ? 0.7 : 1 }} title={muted ? "Ativar som de mensagem" : "Silenciar som de mensagem"} aria-label={muted ? "Ativar som de mensagem" : "Silenciar som de mensagem"}>
        {muted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
      </IconButton>
    </>
  );

  // Encaixado em Assistentes, o cabeçalho é o mesmo das demais áreas (AreaHeader do kit)
  const panelHeader = docked ? (
    <AreaHeader leading={headerBack} avatar={headerAvatar()} title={headerTitle} meta={headerSubtitle} actions={headerActions} />
  ) : (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.1,
        flexShrink: 0,
        background: `linear-gradient(135deg, ${colors.primary} 0%, #0891a0 100%)`,
        color: "#fff",
      }}
    >
      {headerBack}
      {headerAvatar()}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }} noWrap>
          {headerTitle}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", lineHeight: 1.2, opacity: 0.85 }} noWrap>
          {headerSubtitle}
        </Typography>
      </Box>
      {headerActions}
      {!fullScreen && !maximized && (
        <IconButton size="small" onClick={() => setExpanded((v) => !v)} sx={{ color: "#fff" }} title={expanded ? "Reduzir" : "Aumentar"} aria-label={expanded ? "Reduzir" : "Aumentar"}>
          {expanded ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
        </IconButton>
      )}
      {!fullScreen && (
        <IconButton
          size="small"
          onClick={() => setMaximized((v) => !v)}
          sx={{ color: "#fff" }}
          title={maximized ? "Sair da tela cheia (Esc)" : "Tela cheia"}
          aria-label={maximized ? "Sair da tela cheia" : "Tela cheia"}
          aria-pressed={maximized}
        >
          {maximized ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
        </IconButton>
      )}
      <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }} title="Fechar">
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );

  // Corpo: lista | conversa (duas colunas quando expandido ou encaixado)
  const panelBody = (
    <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
      {showList && contactList}
      {showThread && threadPane}
    </Box>
  );

  return (
    <>
      {/* No Assistente do prontuário o botão flutuante some (cobria o campo de mensagem); o chat
          continua abrindo pelo ícone do cabeçalho, que dispara CHAT_OPEN_EVENT. */}
      {!open && !dismissed && !docked && !assistantsOpen && currentPage !== "patient-assistant" && (
        <Tooltip title="Chat" placement="left">
          <Badge
            badgeContent={totalUnread + supportUnread}
            color="error"
            overlap="circular"
            className="no-print"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 1300,
              "& .MuiBadge-badge": {
                fontWeight: 700,
                border: "2px solid #fff",
                minWidth: 22,
                height: 22,
                fontSize: "0.75rem",
                // O Fab do MUI tem z-index próprio (1050) e ficava por cima da bolinha
                zIndex: 1100,
                animation: totalUnread + supportUnread > 0 ? "c4uBlink 1.3s ease-in-out infinite" : "none",
              },
              "@keyframes c4uBlink": { "0%,100%": { opacity: 1, transform: "scale(1) translate(50%, -50%)" }, "50%": { opacity: 0.35, transform: "scale(0.9) translate(50%, -50%)" } },
            }}
          >
            <Fab
              onClick={() => {
                setOpen(true);
                requestDesktopNotifications();
              }}
              sx={{ bgcolor: colors.primary, color: "#fff", boxShadow: "0 6px 18px rgba(3,180,198,0.45)", "&:hover": { bgcolor: "#029AAB" } }}
              aria-label="Abrir chat"
            >
              <Forum />
            </Fab>
          </Badge>
        </Tooltip>
      )}

      {open && !docked && !assistantsOpen && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: coversScreen ? 0 : 24,
            right: coversScreen ? 0 : 24,
            left: coversScreen ? 0 : "auto",
            top: coversScreen ? 0 : "auto",
            width: panelWidth,
            height: panelHeight,
            display: "flex",
            flexDirection: "column",
            borderRadius: coversScreen ? 0 : "16px",
            overflow: "hidden",
            zIndex: 1300,
            boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
          }}
        >
          {panelHeader}
          {panelBody}
        </Paper>
      )}

      {dockEl &&
        createPortal(
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, bgcolor: "#fff" }}>
            {panelHeader}
            {panelBody}
          </Box>,
          dockEl
        )}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Apagar mensagem"
        message={
          conversation?.kind === "group"
            ? "A mensagem sai da conversa para todos os membros do grupo. Ela fica guardada na base da clínica até o prazo de retenção."
            : "A mensagem sai da conversa para você e para a outra pessoa. Ela fica guardada na base da clínica até o prazo de retenção."
        }
        confirmButtonText="Apagar"
        type="danger"
        isProcessing={deleting}
      />

      <NewGroupModal
        open={newGroupOpen}
        contacts={contacts.filter((c) => !c.isSystem)}
        photos={photos}
        onClose={() => setNewGroupOpen(false)}
        onCreated={onGroupCreated}
      />

      {selectedGroup && (
        <GroupMembersModal
          open={membersOpen}
          threadId={selectedGroup.threadId}
          title={selectedGroup.title}
          contacts={contacts.filter((c) => !c.isSystem)}
          photos={photos}
          onClose={() => setMembersOpen(false)}
          onLeft={onLeftGroup}
          onChanged={onGroupChanged}
          onArchived={onLeftGroup}
        />
      )}

      <Dialog open={readersFor !== null} onClose={() => setReadersFor(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem 1.25rem" }}>
          <Typography component="span" sx={{ fontSize: "1.05rem", fontWeight: 600 }}>Quem já leu</Typography>
          <IconButton onClick={() => setReadersFor(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1rem 1.25rem !important" }}>
          {readers === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} sx={{ color: colors.primary }} /></Box>
          ) : (
            <>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: colors.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>Leram ({readers.read.length})</Typography>
              {readers.read.length === 0 && <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, mt: 0.5 }}>Ninguém ainda.</Typography>}
              {readers.read.map((r) => (
                <Box key={r.userId} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.4 }}>
                  <Typography sx={{ fontSize: "0.88rem", color: colors.textPrimary }} noWrap>{r.name}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, flexShrink: 0 }}>{r.readAt ? `${listTime(r.readAt)} ${hourOf(r.readAt)}` : ""}</Typography>
                </Box>
              ))}
              {readers.pending.length > 0 && (
                <>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: colors.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", mt: 1.5 }}>Ainda não ({readers.pending.length})</Typography>
                  {readers.pending.map((r) => (
                    <Typography key={r.userId} sx={{ fontSize: "0.88rem", color: colors.textSecondary, py: 0.4 }} noWrap>{r.name}</Typography>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatLauncher;
